import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { agentQueries } from '@/lib/api/queries/agent'
import { statCardConfig } from '@/lib/constants/agent'
import { StatCard } from '@/components/dashboard/stat-card'
import { StatCardsSkeleton } from '@/components/dashboard/stat-card-skeleton'
import { RecentAgents } from '@/components/dashboard/recent-agents'
import { RecentAgentsSkeleton } from '@/components/dashboard/recent-agent-skeleton'
import { PendingAlert } from '@/components/dashboard/pending-alert'
import type { DashboardStats } from '@/lib/api/types'
import { usePermissions } from '@/lib/hooks/use-permissions'

export const Route = createFileRoute('/_authenticated/')({
  component: DashboardPage,
})

function formatStatValue(key: string, stats: DashboardStats): string | number {
  if (key === 'avg_satisfaction') {
    return stats.avg_satisfaction === null ? String.fromCharCode(0x2014) : `${stats.avg_satisfaction}/5`
  }
  return stats[key as keyof DashboardStats] ?? 0
}

function DashboardPage() {
  const { canCreateAgent } = usePermissions()
  const statsQuery = useQuery(agentQueries.dashboard())
  const recentQuery = useQuery(agentQueries.recent())

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-medium font-serif text-foreground">Welcome back</h1>
        <p className="text-base text-muted-foreground mt-1">
          Manage your AI agents, track performance, and streamline workflows
        </p>
      </div>

      {/* Quick Actions per D-12, D-13 */}
      <div className="flex gap-3 mb-8">
        {canCreateAgent && (
          <Link to="/agents/create">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create Agent
            </Button>
          </Link>
        )}
        <Link to="/agents">
          <Button variant="outline">View All Agents</Button>
        </Link>
      </div>

      {/* Stat Cards per D-01, D-02, D-03 */}
      {statsQuery.isPending ? (
        <div className="mb-8">
          <StatCardsSkeleton />
        </div>
      ) : statsQuery.data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCardConfig.map((card) => (
            <StatCard
              key={card.key}
              title={card.label}
              value={formatStatValue(card.key, statsQuery.data)}
              icon={card.icon}
              iconColor={card.color}
              iconBgColor={card.bgColor}
              to={card.to}
              search={card.search}
            />
          ))}
        </div>
      ) : null}

      {/* Recent Agents per DASH-02 */}
      <div className="mb-8">
        {recentQuery.isPending ? (
          <RecentAgentsSkeleton />
        ) : recentQuery.data ? (
          <RecentAgents agents={recentQuery.data} />
        ) : null}
      </div>

      {/* Pending Alert per D-04 -- only when loaded and count > 0 */}
      {statsQuery.data && statsQuery.data.pending_approvals > 0 && (
        <PendingAlert count={statsQuery.data.pending_approvals} />
      )}
    </div>
  )
}
